// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import {
    HasOnSuccess,
    markSkipOnCompleteIfOnSuccessIsSet,
    replaceInChain,
} from "@insite/client-framework/HandlerCreator";
import { getOrder } from "@insite/client-framework/Services/OrderService";
import siteMessage from "@insite/client-framework/SiteMessage";
import {
    chain,
    ExecuteOnSuccessCallback,
    FetchOrderWithLines,
    PopulateApiParameter,
    ReorderResult,
} from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/Reorder";
import { CartLineCollectionModel, CartLineModel } from "@insite/client-framework/Types/ApiModels";

interface CustomCartLineModel extends CartLineModel {
    properties: {
        warehouseId?: string;
    };
}

export const CustomFetchOrderWithLines: typeof PopulateApiParameter = async props => {
    // XNG-Changes: adding reorder expand
    const order = await getOrder({
        orderNumber: props.parameter.orderNumber,
        expand: ["orderLines", "reorder"] as any,
    });
    props.apiResult = { order };
};

export const CustomPopulateApiParameter: typeof PopulateApiParameter = props => {
    if (!props.apiResult?.order.orderLines?.some(o => !!o.productId)) {
        props.apiResult = undefined;
        props.dispatch({
            type: "Pages/OrderHistory/CompleteReorder",
            orderNumber: props.parameter.orderNumber,
        });
        props.dispatch({
            type: "Context/CompleteUpdatingCart",
        });
        props.parameter.onError?.(siteMessage("OrderHistory_NoProductsAvailableForReorder") as string);
        return false;
    }

    props.apiParameter = {
        cartLines: props.apiResult.order.orderLines.map(
            line =>
                ({
                    productId: line.productId,
                    qtyOrdered: line.qtyOrdered,
                    unitOfMeasure: line.unitOfMeasure,
                    // XNG-Changes: adding warehouseId in the lines
                    properties: {
                        warehouseId: (line as any)?.properties?.warehouseId,
                    },
                } as CustomCartLineModel),
        ),
    } as CartLineCollectionModel;
};

export const XngExecuteOnSuccessCallback: typeof ExecuteOnSuccessCallback = props => {
    markSkipOnCompleteIfOnSuccessIsSet(props);
    // XNG-Changes: Pass apiResult
    const parameter = props.parameter as typeof props.parameter & HasOnSuccess<ReorderResult | undefined>;
    parameter.onSuccess?.(props.apiResult);
};

replaceInChain(chain, PopulateApiParameter, CustomPopulateApiParameter);
replaceInChain(chain, FetchOrderWithLines, CustomFetchOrderWithLines);
replaceInChain(chain, ExecuteOnSuccessCallback, XngExecuteOnSuccessCallback);
